import React, { useState, useEffect } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { useNotification } from '../hooks/useNotification';
import { useUserStore } from '../stores/useUserStore';
import { configs } from '../configs';
import { authenticatedApiCall } from '../utils/authenticatedFetch';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  Trophy,
  Medal,
  Star,
  TrendingUp,
  Users,
  Calendar,
  Award,
  Target,
  Clock,
  Coffee,
} from 'lucide-react';

interface RankingData {
  id: string;
  userId: string;
  userName: string;
  position: number;
  score: number;
  points: number;
  badges: number;
  attendance: number;
  performance: number;
  period: string;
  change: number; // Position change from previous period
}

interface RankingPeriod {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  isActive: boolean;
}

const ModernStaffRanking: React.FC = () => {
  const { translate } = useTranslation();
  const { notify } = useNotification();
  const { user } = useUserStore();

  const [rankings, setRankings] = useState<RankingData[]>([]);
  const [periods, setPeriods] = useState<RankingPeriod[]>([]);
  const [selectedPeriod, setSelectedPeriod] = useState<string>('current');
  const [loading, setLoading] = useState(true);
  const [currentUserRank, setCurrentUserRank] = useState<RankingData | null>(
    null
  );

  useEffect(() => {
    loadRankings();
    loadPeriods();
  }, [selectedPeriod]);

  const loadRankings = async () => {
    setLoading(true);
    try {
      // Fetch rankings from API with authentication
      const apiRankings = await authenticatedApiCall<any[]>(
        `${configs.apiRoot}/staff_rankings`
      );

      // Transform API data to component format
      const rankings: RankingData[] = apiRankings.map(
        (ranking: any, index: number) => ({
          id: ranking._id,
          userId: ranking.userId,
          userName: ranking.userName || 'Unknown User',
          position: index + 1,
          score: ranking.score,
          points: ranking.points,
          badges: ranking.badges,
          attendance: ranking.attendance,
          performance: ranking.performance,
          period: selectedPeriod,
          change: ranking.change || 0,
        })
      );

      setRankings(rankings);

      // Find current user's rank
      const userRank = rankings.find((r) => r.userId === user?.id);
      setCurrentUserRank(userRank || null);
    } catch (error) {
      console.error('Error loading rankings:', error);
      notify('Failed to load rankings', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const loadPeriods = async () => {
    try {
      // Mock data for periods
      const mockPeriods: RankingPeriod[] = [
        {
          id: 'current',
          name: 'Current Month',
          startDate: '2024-01-01',
          endDate: '2024-01-31',
          isActive: true,
        },
        {
          id: 'last',
          name: 'Last Month',
          startDate: '2023-12-01',
          endDate: '2023-12-31',
          isActive: false,
        },
        {
          id: 'quarter',
          name: 'This Quarter',
          startDate: '2024-01-01',
          endDate: '2024-03-31',
          isActive: false,
        },
      ];

      setPeriods(mockPeriods);
    } catch (error) {
      console.error('Error loading periods:', error);
    }
  };

  const getPositionIcon = (position: number) => {
    switch (position) {
      case 1:
        return <Trophy className="h-5 w-5 text-yellow-500" />;
      case 2:
        return <Medal className="h-5 w-5 text-gray-400" />;
      case 3:
        return <Award className="h-5 w-5 text-amber-600" />;
      default:
        return (
          <span className="text-lg font-bold text-muted-foreground">
            #{position}
          </span>
        );
    }
  };

  const getChangeIcon = (change: number) => {
    if (change > 0) {
      return <TrendingUp className="h-4 w-4 text-green-500" />;
    } else if (change < 0) {
      return <TrendingUp className="h-4 w-4 text-red-500 rotate-180" />;
    }
    return null;
  };

  const getChangeText = (change: number) => {
    if (change > 0) {
      return `+${change}`;
    } else if (change < 0) {
      return `${change}`;
    }
    return 'No change';
  };

  const getChangeColor = (change: number) => {
    if (change > 0) return 'text-green-600';
    if (change < 0) return 'text-red-600';
    return 'text-muted-foreground';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p>Loading rankings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 py-6">
      <div className="flex flex-col items-start">
        <h1 className="text-3xl font-bold text-foreground">Staff Rankings</h1>
        <p className="text-muted-foreground mt-2">
          See how you rank among your colleagues
        </p>
      </div>

      {/* Period Selection */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Ranking Period
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {periods.map((period) => (
              <Button
                key={period.id}
                variant={selectedPeriod === period.id ? 'default' : 'outline'}
                onClick={() => setSelectedPeriod(period.id)}
                className="flex items-center gap-2"
              >
                {period.name}
                {period.isActive && (
                  <Badge variant="secondary" className="ml-2">
                    Active
                  </Badge>
                )}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Current User Rank */}
      {currentUserRank && (
        <Card className="border-primary bg-primary/5">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Star className="h-5 w-5 text-primary" />
              Your Current Rank
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                {getPositionIcon(currentUserRank.position)}
                <div>
                  <div className="font-semibold text-lg">
                    #{currentUserRank.position} of {rankings.length}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {currentUserRank.userName}
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-primary">
                  {currentUserRank.score}%
                </div>
                <div className="text-sm text-muted-foreground">
                  Overall Score
                </div>
              </div>
            </div>

            <div className="grid grid-cols-4 gap-4 mt-4">
              <div className="text-center">
                <div className="text-lg font-semibold text-yellow-600">
                  {currentUserRank.points}
                </div>
                <div className="text-xs text-muted-foreground">Points</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-semibold text-blue-600">
                  {currentUserRank.badges}
                </div>
                <div className="text-xs text-muted-foreground">Badges</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-semibold text-green-600">
                  {currentUserRank.attendance}%
                </div>
                <div className="text-xs text-muted-foreground">Attendance</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-semibold text-purple-600">
                  {currentUserRank.performance}%
                </div>
                <div className="text-xs text-muted-foreground">Performance</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Rankings List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Team Rankings
          </CardTitle>
          <CardDescription>
            Rankings based on points, badges, attendance, and performance
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {rankings.map((ranking, index) => (
              <div
                key={ranking.id}
                className={`flex items-center justify-between p-4 rounded-lg border ${
                  ranking.userId === user?.id
                    ? 'border-primary bg-primary/5'
                    : 'border-border'
                }`}
              >
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    {getPositionIcon(ranking.position)}
                    {ranking.change !== 0 && (
                      <div
                        className={`flex items-center gap-1 ${getChangeColor(
                          ranking.change
                        )}`}
                      >
                        {getChangeIcon(ranking.change)}
                        <span className="text-sm">
                          {getChangeText(ranking.change)}
                        </span>
                      </div>
                    )}
                  </div>

                  <div>
                    <div className="font-semibold">
                      {ranking.userName}
                      {ranking.userId === user?.id && (
                        <Badge variant="secondary" className="ml-2">
                          You
                        </Badge>
                      )}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {ranking.points} points • {ranking.badges} badges
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-6">
                  <div className="text-center">
                    <div className="text-lg font-semibold">
                      {ranking.score}%
                    </div>
                    <div className="text-xs text-muted-foreground">Overall</div>
                  </div>

                  <div className="w-20">
                    <Progress value={ranking.score} className="h-2" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Performance Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Target className="h-4 w-4" />
              Top Performers
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {rankings.slice(0, 3).map((ranking) => (
                <div
                  key={ranking.id}
                  className="flex items-center justify-between"
                >
                  <span className="text-sm">{ranking.userName}</span>
                  <Badge variant="outline">{ranking.score}%</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Best Attendance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {rankings
                .sort((a, b) => b.attendance - a.attendance)
                .slice(0, 3)
                .map((ranking) => (
                  <div
                    key={ranking.id}
                    className="flex items-center justify-between"
                  >
                    <span className="text-sm">{ranking.userName}</span>
                    <Badge variant="outline">{ranking.attendance}%</Badge>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Coffee className="h-4 w-4" />
              Most Badges
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {rankings
                .sort((a, b) => b.badges - a.badges)
                .slice(0, 3)
                .map((ranking) => (
                  <div
                    key={ranking.id}
                    className="flex items-center justify-between"
                  >
                    <span className="text-sm">{ranking.userName}</span>
                    <Badge variant="outline">{ranking.badges}</Badge>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ModernStaffRanking;
